import React, { useState, useEffect, useMemo } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { GLOBAL_BW, formatBandwidthFromMbpsInline } from "../../backbone.data";
import { UsageBar } from "../BackboneCharts";
import {
	BandwidthChartSimple,
	type BandwidthChartSimplePoint,
} from "./BandwidthChartSimple";
import {
	fetchBbConnectorPerformance,
	fetchBbConnectorBgp,
	fetchBbConnectorRoutes,
	type BbConnectorListItem,
	type BbConnectorBgpResponseData,
	type BbConnectorPerformanceData,
	type BbConnectorPerformancePoint,
	type BbConnectorRoutesResponseData,
} from "../../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { ReactComponent as IconInfo } from "../../../../../shared/assets/Icons/circle-info-solid.svg";
import {
	buildPredicate,
	type FilterField,
	labelId,
	parseToken,
	type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import GenericButton from "@ui-components/Button/GenericButton";

export interface ConnectorsModalProps {
	connector: BbConnectorListItem | null;
	onClose: () => void;
	tenantId: number | null;
}

const CONNECTOR_DETAIL_TABS = [
	{ label: "General Information", value: "general" },
	{ label: "BGP Session & Routes", value: "bgp-routes" },
];

const roundBw = (v: number): number => Math.round(v * 1000) / 1000;

/** Performance API query window (BB-Connector performance endpoint). */
const CONNECTOR_PERF_TIME_RANGE = "6h";
const CONNECTOR_PERF_INTERVAL = "5m";

/** Modal title: `name - subscriptionId` (detail first, else root). */
function connectorModalTitle(c: BbConnectorListItem): string {
	const name = (c.name ?? "").trim() || "Connector";
	const d = c.connectedServiceDetail;
	let sub = "";
	if (
		d &&
		typeof d === "object" &&
		d.subscriptionId != null &&
		String(d.subscriptionId).trim() !== ""
	) {
		sub = String(d.subscriptionId).trim();
	} else if (c.subscriptionId != null && String(c.subscriptionId).trim() !== "") {
		sub = String(c.subscriptionId).trim();
	}
	return sub !== "" ? `${name} - ${sub}` : name;
}

function formatBgpUptime(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds < 0) return "—";
	const d = Math.floor(seconds / 86400);
	const h = Math.floor((seconds % 86400) / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const parts: string[] = [];
	if (d > 0) parts.push(`${d}d`);
	if (h > 0 || d > 0) parts.push(`${h}h`);
	parts.push(`${m}m`);
	return parts.join(" ");
}

function mergePerformanceSeriesToChart(
	inbound: BbConnectorPerformancePoint[],
	outbound: BbConnectorPerformancePoint[]
): BandwidthChartSimplePoint[] {
	const byTime = new Map<string, { inbound?: number; outbound?: number }>();
	for (const p of inbound) {
		const cur = byTime.get(p.time) ?? {};
		cur.inbound = p.value;
		byTime.set(p.time, cur);
	}
	for (const p of outbound) {
		const cur = byTime.get(p.time) ?? {};
		cur.outbound = p.value;
		byTime.set(p.time, cur);
	}
	const sorted = Array.from(byTime.keys()).sort(
		(a, b) => new Date(a).getTime() - new Date(b).getTime()
	);
	return sorted.map((time) => {
		const row = byTime.get(time) ?? {};
		const d = new Date(time);
		const formattedTime = Number.isNaN(d.getTime())
			? time
			: d.toLocaleTimeString(undefined, {
					hour: "2-digit",
					minute: "2-digit",
				});
		return {
			formattedTime,
			inbound: row.inbound,
			outbound: row.outbound,
		};
	});
}

const ConnectorsModal: React.FC<ConnectorsModalProps> = ({
	connector,
	onClose,
	tenantId,
}) => {
	const api = useAxiosAuth0();
	const [detailModalTab, setDetailModalTab] = useState("general");
	const [perfLoading, setPerfLoading] = useState(false);
	const [perfError, setPerfError] = useState<string | null>(null);
	const [perfData, setPerfData] = useState<BbConnectorPerformanceData | null>(
		null
	);
	const [perfRetryToken, setPerfRetryToken] = useState(0);
	const [bgpLoading, setBgpLoading] = useState(false);
	const [bgpError, setBgpError] = useState<string | null>(null);
	const [bgpData, setBgpData] = useState<BbConnectorBgpResponseData | null>(
		null
	);
	const [bgpRetryToken, setBgpRetryToken] = useState(0);
	const [modalRouteFilters, setModalRouteFilters] = useState<ParsedFilter[]>(
		[]
	);
	const [routesLoading, setRoutesLoading] = useState(false);
	const [routesError, setRoutesError] = useState<string | null>(null);
	const [routesData, setRoutesData] =
		useState<BbConnectorRoutesResponseData | null>(null);
	const [routesRetryToken, setRoutesRetryToken] = useState(0);

	useEffect(() => {
		if (connector != null) {
			setDetailModalTab("general");
			setModalRouteFilters([]);
		}
	}, [connector?.id]);

	useEffect(() => {
		if (connector == null || tenantId == null) {
			setPerfData(null);
			setPerfError(null);
			setPerfLoading(false);
			return;
		}
		const connectorId = connector.id;
		let cancelled = false;
		setPerfLoading(true);
		setPerfError(null);
		fetchBbConnectorPerformance(api, tenantId, connectorId, {
			timeRange: CONNECTOR_PERF_TIME_RANGE,
			interval: CONNECTOR_PERF_INTERVAL,
		})
			.then((res) => {
				if (cancelled) return;
				const body = res.data;
				if (body?.success && body.data) {
					setPerfData(body.data);
					setPerfError(null);
				} else {
					setPerfData(null);
					setPerfError(body?.message ?? "Failed to load performance metrics");
				}
			})
			.catch((err) => {
				if (cancelled) return;
				setPerfData(null);
				setPerfError(
					err instanceof Error ? err.message : "Failed to load performance metrics"
				);
			})
			.finally(() => {
				if (!cancelled) setPerfLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [connector, tenantId, api, perfRetryToken]);

	useEffect(() => {
		if (connector == null || tenantId == null) {
			setBgpData(null);
			setBgpError(null);
			setBgpLoading(false);
			return;
		}
		const connectorId = connector.id;
		let cancelled = false;
		setBgpLoading(true);
		setBgpError(null);
		fetchBbConnectorBgp(api, tenantId, connectorId)
			.then((res) => {
				if (cancelled) return;
				const body = res.data;
				if (body?.success && body.data) {
					setBgpData(body.data);
					setBgpError(null);
				} else {
					setBgpData(null);
					setBgpError(body?.message ?? "Failed to load BGP session");
				}
			})
			.catch((err) => {
				if (cancelled) return;
				setBgpData(null);
				setBgpError(
					err instanceof Error ? err.message : "Failed to load BGP session"
				);
			})
			.finally(() => {
				if (!cancelled) setBgpLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [connector, tenantId, api, bgpRetryToken]);

	useEffect(() => {
		if (connector == null || tenantId == null) {
			setRoutesData(null);
			setRoutesError(null);
			setRoutesLoading(false);
			return;
		}
		const connectorId = connector.id;
		let cancelled = false;
		setRoutesLoading(true);
		setRoutesError(null);
		fetchBbConnectorRoutes(api, tenantId, connectorId)
			.then((res) => {
				if (cancelled) return;
				const body = res.data;
				if (body?.success && body.data) {
					setRoutesData(body.data);
					setRoutesError(null);
				} else {
					setRoutesData(null);
					setRoutesError(body?.message ?? "Failed to load routes");
				}
			})
			.catch((err) => {
				if (cancelled) return;
				setRoutesData(null);
				setRoutesError(
					err instanceof Error ? err.message : "Failed to load routes"
				);
			})
			.finally(() => {
				if (!cancelled) setRoutesLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [connector, tenantId, api, routesRetryToken]);

	const performanceChartData = useMemo(() => {
		const epId = connector?.id;
		if (
			epId == null ||
			perfData?.connectorId !== epId ||
			!perfData.bandwidthUtilization
		) {
			return [];
		}
		const { inbound, outbound } = perfData.bandwidthUtilization;
		return mergePerformanceSeriesToChart(inbound ?? [], outbound ?? []);
	}, [connector?.id, perfData]);

	const routeTableRows = useMemo(() => {
		const id = connector?.id;
		if (id == null || routesData?.connectorId !== id || !routesData.routes) {
			return [];
		}
		return routesData.routes.map((r, idx) => ({
			prefix: r.prefix,
			lp: r.lp,
			med: r.med,
			asPath: r.asPath != null && r.asPath !== "" ? r.asPath : "—",
			best: r.best,
			origin: r.origin != null && r.origin !== "" ? r.origin : "—",
			community:
				r.community != null && r.community !== "" ? r.community : "—",
			direction: r.direction != null && r.direction !== "" ? r.direction : "—",
			rowKey: `${r.prefix}|${r.direction ?? ""}|${idx}`,
		}));
	}, [connector?.id, routesData]);

	return (
		<GenericModal
			isOpen={connector !== null}
			onClose={onClose}
			title={
				connector ? (
					<strong>{connectorModalTitle(connector)}</strong>
				) : undefined
			}
			size="lg"
			customWidth="max-w-7xl"
			minHeight="min(850px, 80vh)"
			maxHeight="90vh"
			footer={
				<GenericButton
					label="Close"
					variant="outlined"
					onClick={onClose}
					className="mb-10"
				/>
			}
		>
			{connector &&
				(() => {
					const ep = connector;
					const inbound = ep.bandwidth?.inbound ?? 0;
					const outbound = ep.bandwidth?.outbound ?? 0;
					const bwTotal = inbound + outbound;
					const listUnit = ep.bandwidth?.unit ?? "Mbps";
					const perfMatchesRow =
						perfData != null && perfData.connectorId === ep.id;
					const modalPortCapacityTotal =
						perfMatchesRow && typeof perfData.capacity === "number"
							? perfData.capacity
							: (ep.capacity ?? null);
					const bu = perfMatchesRow ? perfData.bandwidthUtilization : undefined;
					const legacyPerfUnit =
						bu?.unit != null && String(bu.unit).trim() !== ""
							? String(bu.unit).trim()
							: null;
					const inboundChartUnit =
						bu?.inboundUnit != null && String(bu.inboundUnit).trim() !== ""
							? String(bu.inboundUnit).trim()
							: legacyPerfUnit ?? listUnit;
					const outboundChartUnit =
						bu?.outboundUnit != null && String(bu.outboundUnit).trim() !== ""
							? String(bu.outboundUnit).trim()
							: legacyPerfUnit ?? listUnit;
					const perfBandwidthSeriesEmpty =
						perfMatchesRow &&
						bu != null &&
						(bu.inbound?.length ?? 0) === 0 &&
						(bu.outbound?.length ?? 0) === 0;
					const routesMatchesRow =
						routesData != null && routesData.connectorId === ep.id;
					return (
						<div className="py-2 min-h-[480px]">
							<GenericTabs
								tabs={CONNECTOR_DETAIL_TABS}
								activeTab={detailModalTab}
								onTabChange={setDetailModalTab}
								size="sm"
								className="mb-10"
							/>
							{detailModalTab === "general" && (
								<div>
									{perfLoading ? (
										<>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
												<GenericCard
													isCollapsible={false}
													variant="secondary"
													bodyContent={
														<>
															<div className="flex justify-between items-center text-xs mb-1 gap-2">
																<span className="text-semantic-text-primary font-medium">
																	Port Capacity
																</span>
																<SkeletonBlock
																	h={14}
																	w={120}
																	className="rounded flex-shrink-0"
																/>
															</div>
															<SkeletonBlock h={8} className="rounded w-full" />
														</>
													}
												/>
												<GenericCard
													isCollapsible={false}
													variant="secondary"
													bodyContent={
														<>
															<div className="flex justify-between items-center text-xs mb-1 gap-2">
																<span className="text-semantic-text-primary font-medium">
																	Global Backbone
																</span>
																<SkeletonBlock
																	h={14}
																	w={120}
																	className="rounded flex-shrink-0"
																/>
															</div>
															<SkeletonBlock h={8} className="rounded w-full" />
														</>
													}
												/>
											</div>
											<h3 className="text-sm font-medium text-semantic-text-primary mb-3">
												Bandwidth
											</h3>
											<div className="grid grid-cols-3 gap-2 mb-4">
												{(["Current", "Inbound", "Outbound"] as const).map(
													(label) => (
														<div
															key={label}
															className="rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-2 text-center"
														>
															<div className="text-xs text-semantic-text-secondary">
																{label}
															</div>
															<SkeletonBlock
																h={28}
																w={72}
																className="rounded mx-auto mt-2"
															/>
														</div>
													)
												)}
											</div>
										</>
									) : (
										<>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
												<GenericCard
													isCollapsible={false}
													variant="secondary"
													bodyContent={
														<>
															<div className="flex justify-between text-xs mb-1">
																<span className="text-semantic-text-primary font-medium">
																	Port Capacity
																</span>
																<span className="text-semantic-text-secondary">
																	{formatBandwidthFromMbpsInline(roundBw(bwTotal))} /{" "}
																	{modalPortCapacityTotal != null &&
																	modalPortCapacityTotal > 0
																		? formatBandwidthFromMbpsInline(modalPortCapacityTotal)
																		: "—"}
																</span>
															</div>
															{modalPortCapacityTotal != null &&
															modalPortCapacityTotal > 0 ? (
																<UsageBar
																	used={bwTotal}
																	cap={modalPortCapacityTotal}
																	height={8}
																/>
															) : (
																<div className="h-2 rounded bg-semantic-bg-tertiary" />
															)}
														</>
													}
												/>
												<GenericCard
													isCollapsible={false}
													variant="secondary"
													bodyContent={
														<>
															<div className="flex justify-between text-xs mb-1">
																<span className="text-semantic-text-primary font-medium">
																	Global Backbone
																</span>
																<span className="text-semantic-text-secondary">
																	{formatBandwidthFromMbpsInline(roundBw(bwTotal))} /{" "}
																	{formatBandwidthFromMbpsInline(GLOBAL_BW)}
																</span>
															</div>
															<UsageBar used={bwTotal} cap={GLOBAL_BW} height={8} />
														</>
													}
												/>
											</div>

											<h3 className="text-sm font-medium text-semantic-text-primary mb-3">
												Bandwidth
											</h3>
											<div className="grid grid-cols-3 gap-2 mb-4">
												<div className="rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-2 text-center">
													<div className="text-xs text-semantic-text-secondary">
														Current
													</div>
													<div className="text-lg font-bold text-semantic-text-brand">
														{perfBandwidthSeriesEmpty
															? "—"
															: formatBandwidthFromMbpsInline(roundBw(bwTotal))}
													</div>
												</div>
												<div className="rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-2 text-center">
													<div className="text-xs text-semantic-text-secondary">
														Inbound
													</div>
													<div className="text-lg font-bold text-semantic-text-brand">
														{perfBandwidthSeriesEmpty
															? "—"
															: formatBandwidthFromMbpsInline(inbound ?? 0)}
													</div>
												</div>
												<div className="rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-2 text-center">
													<div className="text-xs text-semantic-text-secondary">
														Outbound
													</div>
													<div className="text-lg font-bold text-semantic-text-brand">
														{perfBandwidthSeriesEmpty
															? "—"
															: formatBandwidthFromMbpsInline(outbound ?? 0)}
													</div>
												</div>
											</div>
										</>
									)}

									<div className="mt-4">
										<h3 className="text-sm font-medium text-semantic-text-primary mb-3">
											{perfLoading
												? "Bandwidth trend"
												: perfMatchesRow && bu?.timeRange
													? `${bu.timeRange} Trend`
													: "Bandwidth trend"}
										</h3>
										<div className="relative rounded-md border border-semantic-border-primary bg-semantic-bg-secondary min-h-[220px]">
											{perfLoading && (
												<div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
													<span className="text-lg font-semibold text-gray-700">
														Loading...
													</span>
												</div>
											)}
											{!perfLoading && perfError != null && (
												<div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4 py-8">
													<IconInfo className="w-8 h-8 text-yellow-500 flex-shrink-0" />
													<p className="mt-2 text-sm text-semantic-text-secondary text-center max-w-md">
														Performance data is unavailable at the moment.
													</p>
													<button
														type="button"
														onClick={() => setPerfRetryToken((t) => t + 1)}
														className="mt-4 text-sm font-medium text-semantic-text-brand hover:underline"
													>
														Retry
													</button>
												</div>
											)}
											{!perfLoading && perfError == null && (
												<div className="relative w-full p-2">
													<BandwidthChartSimple
														data={performanceChartData}
														height={220}
														unit={listUnit}
														inboundUnit={
															perfMatchesRow && bu != null
																? inboundChartUnit
																: undefined
														}
														outboundUnit={
															perfMatchesRow && bu != null
																? outboundChartUnit
																: undefined
														}
													/>
													{perfMatchesRow &&
														performanceChartData.length === 0 && (
															<div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
																<IconInfo className="w-8 h-8 text-yellow-500" />
																<p className="mt-2 text-sm text-semantic-text-secondary text-center px-4">
																	Performance data is unavailable at the moment.
																</p>
															</div>
														)}
												</div>
											)}
										</div>
									</div>
								</div>
							)}
							{detailModalTab === "bgp-routes" &&
								(() => {
									const bgpMatchesRow =
										bgpData != null && bgpData.connectorId === ep.id;
									const s = bgpMatchesRow ? bgpData.bgpSession : null;
									const leftRows: [string, string][] = s
										? [
												["Local IP", s.localIp ?? "—"],
												["Peer IP", s.peerIp ?? "—"],
												["Local ASN", String(s.localAsn)],
												["Remote ASN", String(s.remoteAsn)],
												[
													"Keepalive",
													`${s.keepalive}s (cfg ${s.configuredKeepalive}s)`,
												],
												["Hold", `${s.hold}s (cfg ${s.configuredHold}s)`],
											]
										: [
												["Local IP", "—"],
												["Peer IP", ep.bgpNeighborIp ?? "—"],
												["Local ASN", "—"],
												["Remote ASN", "—"],
												["Keepalive", "—"],
												["Hold", "—"],
											];
									// eslint-disable-next-line
									const hostConfigFromList =
										ep.hostConfig?.length
											? ep.hostConfig
													.map(
														(h) =>
															`${h.host} / ${h.interface ?? h.interfaceName ?? "—"}`
													)
													.join(", ")
											: "—";
									const rightRows: [string, string][] = s
										? [
												// ["MD5", s.md5 != null && s.md5 !== "" ? s.md5 : "—"],
												["Uptime", formatBgpUptime(s.uptime)],
												["State", s.state ?? "—"],
												["Established", s.established ? "Yes" : "No"],
												// ["Host config", s.hostConfig || "—"],
												["Rx Prefixes", String(s.rxPrefixes)],
												["Tx Prefixes", String(s.txPrefixes)],
												// ["Router", s.router ?? "—"],
												// ["State", s.state ?? "—"],
												// ["Established", s.established ? "Yes" : "No"],
												// ["Host config", s.hostConfig || "—"],
											]
										: [
												// ["MD5", "—"],
												["Uptime", "—"],
												["Rx Prefixes", "—"],
												["Tx Prefixes", "—"],
												["Router", "—"],
												// ["State", ep.bgpStatus?.state ?? "—"],
												// [
												// 	"Established",
												// 	ep.bgpStatus?.isEstablished ? "Yes" : "No",
												// ],
												// ["Host config", hostConfigFromList],
											];
									return (
										<div className="flex w-full flex-col gap-6 py-2">
											<GenericCard
												isCollapsible
												initialExpanded
												variant="primary"
												shadow="flat"
												className="w-full"
												headerContent={
													<span className="text-sm font-semibold text-semantic-text-primary">
														BGP Session
													</span>
												}
												bodyContent={
													<>
														{bgpLoading && (
															<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
																<GenericCard
																	isCollapsible={false}
																	className="w-full"
																	bodyContent={
																		<div className="space-y-2">
																			{Array.from({ length: 7 }).map((_, i) => (
																				<div
																					key={i}
																					className="flex justify-between gap-2 py-1 border-b border-semantic-border-primary"
																				>
																					<SkeletonBlock h={16} w={80} className="rounded" />
																					<SkeletonBlock
																						h={16}
																						w={120}
																						className="rounded flex-shrink-0"
																					/>
																				</div>
																			))}
																		</div>
																	}
																/>
																<GenericCard
																	isCollapsible={false}
																	className="w-full"
																	bodyContent={
																		<div className="space-y-2">
																			{Array.from({ length: 7 }).map((_, i) => (
																				<div
																					key={i}
																					className="flex justify-between gap-2 py-1 border-b border-semantic-border-primary"
																				>
																					<SkeletonBlock h={16} w={80} className="rounded" />
																					<SkeletonBlock
																						h={16}
																						w={120}
																						className="rounded flex-shrink-0"
																					/>
																				</div>
																			))}
																		</div>
																	}
																/>
															</div>
														)}
														{!bgpLoading && bgpError != null && (
															<div className="flex flex-col items-center justify-center py-12 px-4 rounded-md border border-semantic-border-primary bg-semantic-bg-secondary">
																<IconInfo className="w-8 h-8 text-yellow-500 flex-shrink-0" />
																<p className="mt-2 text-sm text-semantic-text-secondary text-center max-w-md">
																	{bgpError}
																</p>
																<button
																	type="button"
																	onClick={() => setBgpRetryToken((t) => t + 1)}
																	className="mt-4 text-sm font-medium text-semantic-text-brand hover:underline"
																>
																	Retry
																</button>
															</div>
														)}
														{!bgpLoading && bgpError == null && (
															<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
																<GenericCard
																	isCollapsible={false}
																	className="w-full"
																	bodyContent={
																		<div className="space-y-0">
																			{leftRows.map(([k, v]) => (
																				<div
																					key={k}
																					className="flex justify-between w-full py-1 border-b border-semantic-border-primary gap-2"
																				>
																					<span className="text-sm text-semantic-text-primary flex-shrink-0">
																						{k}
																					</span>
																					<span className="text-sm text-semantic-text-secondary break-all text-right">
																						{v}
																					</span>
																				</div>
																			))}
																		</div>
																	}
																/>
																<GenericCard
																	isCollapsible={false}
																	className="w-full"
																	bodyContent={
																		<div className="space-y-0">
																			{rightRows.map(([k, v]) => (
																				<div
																					key={k}
																					className="flex justify-between w-full py-1 border-b border-semantic-border-primary gap-2"
																				>
																					<span className="text-sm text-semantic-text-primary flex-shrink-0">
																						{k}
																					</span>
																					<span className="text-sm text-semantic-text-secondary break-all text-right">
																						{v}
																					</span>
																				</div>
																			))}
																		</div>
																	}
																/>
															</div>
														)}
													</>
												}
											/>

											{/* Routes block (filter + table) */}
											{(() => {
									type RouteRow = {
										prefix: string;
										lp: number;
										med: number;
										asPath: string;
										best: boolean;
										origin: string;
										community: string;
										direction: string;
										rowKey: string;
									};
									const routeFields: FilterField<RouteRow>[] = [
										{
											label: "Prefixes",
											key: "prefixes",
											accessor: (r: RouteRow) => r.prefix,
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "LP",
											key: "lp",
											accessor: (r: RouteRow) => String(r.lp),
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "Med",
											key: "med",
											accessor: (r: RouteRow) => String(r.med),
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "As Path",
											key: "aspath",
											accessor: (r: RouteRow) => r.asPath,
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "Origin",
											key: "origin",
											accessor: (r: RouteRow) => r.origin,
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "Community",
											key: "community",
											accessor: (r: RouteRow) => r.community,
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "Direction",
											key: "direction",
											accessor: (r: RouteRow) => r.direction,
											type: "string" as const,
											inFreeText: true,
										},
										{
											label: "BE",
											key: "be",
											accessor: (r: RouteRow) => (r.best ? "Yes" : "No"),
											type: "string" as const,
											inFreeText: true,
										},
									].map((f) => ({ ...f, key: f.key ?? labelId(f.label) }));
									const routeKeyMap = new Map<string, string>();
									routeFields.forEach((f) => {
										const id = (f.key ?? labelId(f.label)).toLowerCase();
										routeKeyMap.set(id, id);
										routeKeyMap.set(f.label.toLowerCase(), id);
									});
									const routeNorm = modalRouteFilters.map((f) => {
										const token = f.op
											? { ...f }
											: parseToken(f.key ? `${f.key}=${f.value}` : f.value);
										if (token.key) {
											const k = token.key.trim().toLowerCase();
											token.key = routeKeyMap.get(k) ?? labelId(token.key);
										}
										return token;
									});
									const routePredicate = buildPredicate<RouteRow>(
										routeNorm,
										routeFields
									);
									const sourceRows = routesMatchesRow
										? routeTableRows
										: [];
									const filteredRoutes = sourceRows.filter(routePredicate);
									const ROUTE_TABLE_HEADERS = [
										"Prefixes",
										"LP",
										"Med",
										"As Path",
										"Origin",
										"Community",
										"Direction",
										"BE",
									];
									return (
										<section className="w-full">
											<h3 className="mb-3 text-sm font-medium text-semantic-text-primary">
												Routes
											</h3>
											<GenericCard
												isCollapsible={false}
												className="w-full"
												bodyContent={
													<div className="flex flex-col min-h-0">
														{routesMatchesRow &&
															routesData != null && (
																<p className="text-xs text-semantic-text-secondary mb-3">
																	VRF {routesData.vrf.displayName}
																</p>
															)}
														{routesLoading && (
															<div className="space-y-2 mb-4">
																<SkeletonBlock h={40} className="rounded w-full max-w-[500px]" />
																{Array.from({ length: 5 }).map((_, i) => (
																	<SkeletonBlock
																		key={i}
																		h={36}
																		className="rounded w-full"
																	/>
																))}
															</div>
														)}
														{!routesLoading && routesError != null && (
															<div className="flex flex-col items-center justify-center py-10 px-4 rounded-md border border-semantic-border-primary bg-semantic-bg-secondary mb-4">
																<IconInfo className="w-8 h-8 text-yellow-500 flex-shrink-0" />
																<p className="mt-2 text-sm text-semantic-text-secondary text-center max-w-md">
																	{routesError}
																</p>
																<button
																	type="button"
																	onClick={() =>
																		setRoutesRetryToken((t) => t + 1)
																	}
																	className="mt-4 text-sm font-medium text-semantic-text-brand hover:underline"
																>
																	Retry
																</button>
															</div>
														)}
														{!routesLoading && routesError == null && (
															<>
																<div className="mb-4 flex-shrink-0">
																	<div className="w-full max-w-full sm:max-w-[500px]">
																		<GenericSearchbar
																			placeholder="Find by attribute or value"
																			suggestedKeys={ROUTE_TABLE_HEADERS}
																			onFiltersChange={setModalRouteFilters}
																		/>
																	</div>
																</div>
																<div className="min-w-0">
																	<GenericTable<RouteRow>
																		columns={[
																			{
																				header: "Prefixes",
																				renderCell: (r) => (
																					<span className="font-mono font-medium text-semantic-text-primary">
																						{r.prefix}
																					</span>
																				),
																			},
																			{
																				header: "LP",
																				renderCell: (r) => (
																					<span
																						className={
																							r.lp !== 100
																								? "font-medium text-semantic-text-brand"
																								: "text-semantic-text-secondary"
																						}
																					>
																						{r.lp}
																					</span>
																				),
																			},
																			{
																				header: "Med",
																				accessor: "med",
																			},
																			{
																				header: "As Path",
																				renderCell: (r) => (
																					<span className="font-mono text-sm text-semantic-text-secondary break-all">
																						{r.asPath}
																					</span>
																				),
																			},
																			{
																				header: "Origin",
																				renderCell: (r) => (
																					<span className="text-sm text-semantic-text-secondary">
																						{r.origin}
																					</span>
																				),
																			},
																			{
																				header: "Community",
																				renderCell: (r) => (
																					<span className="font-mono text-xs text-semantic-text-secondary break-all">
																						{r.community}
																					</span>
																				),
																			},
																			{
																				header: "Direction",
																				renderCell: (r) => (
																					<span className="text-sm text-semantic-text-secondary">
																						{r.direction}
																					</span>
																				),
																			},
																			{
																				header: "BE",
																				renderCell: (r) => (
																					<span
																						className={
																							r.best
																								? "font-medium text-semantic-text-brand"
																								: "text-semantic-text-secondary"
																						}
																					>
																						{r.best ? "\u2713" : "\u2014"}
																					</span>
																				),
																			},
																		]}
																		data={filteredRoutes}
																		rowKey={(r) => r.rowKey}
																		isFooterVisible={true}
																		// Cap the routes table body height so heavy result
																		// sets scroll vertically inside the table instead of
																		// growing the modal past the viewport. `min()` keeps
																		// it responsive: 320px on tall screens, 35vh on short
																		// ones (where 320px would push BGP + header off).
																		maxTableBodyHeight="min(320px, 35vh)"
																		initialRowsPerPage={20}
																		rowsPerPageOptions={[10, 20, 30]}
																	/>
																</div>
															</>
														)}
													</div>
												}
											/>
										</section>
									);
								})()}
										</div>
									);
								})()}
						</div>
					);
				})()}
		</GenericModal>
	);
};

export default ConnectorsModal;
